import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useBatch, pizzaCount, pizzasLeft } from "@/context/BatchContext";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "biga_cart";
const CART_FIELDS = ["key", "item_id", "name", "size", "crust", "extras", "half_and_half", "qty", "unit_price", "image"];

function sanitizeCart(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item && typeof item === "object" && typeof item.key === "string" && typeof item.item_id === "string").map((item) => {
    const line = Object.fromEntries(CART_FIELDS.filter((field) => field in item).map((field) => [field, item[field]]));
    return { ...line, qty: Math.max(1, Number(line.qty) || 1), extras: Array.isArray(line.extras) ? line.extras.filter((extra) => typeof extra === "string") : [] };
  });
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return sanitizeCart(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []);
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [checkoutIntent, setCheckoutIntent] = useState(null); // null | "whatsapp"

  const startWhatsAppCheckout = useCallback(() => {
    setCheckoutIntent("whatsapp");
    setOpen(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizeCart(items)));
  }, [items]);

  const { batch: liveBatch } = useBatch();
  const ref = useRef({ items, batch: liveBatch });
  ref.current = { items, batch: liveBatch };
  const fitsBatch = useCallback((itemId, extra) => {
    const { items, batch } = ref.current;
    const left = pizzasLeft(batch);
    if (left === null || !(batch?.pizza_item_ids || []).includes(itemId)) return true;
    if (pizzaCount(items, batch) + extra <= left) return true;
    toast.error(left === 0 ? "Pizzas are sold out right now." : `Only ${left} pizza${left === 1 ? "" : "s"} left. You already have them all in your cart.`, { id: "batch-limit" });
    return false;
  }, []);

  const addItem = useCallback((line) => {
    if (!fitsBatch(line.item_id, line.qty)) return false;
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.key === line.key);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + line.qty };
        return next;
      }
      return [...prev, line];
    });
    return true;
  }, [fitsBatch]);

  const updateQty = useCallback((key, delta) => {
    const line = ref.current.items.find((p) => p.key === key);
    if (delta > 0 && line && !fitsBatch(line.item_id, delta)) return;
    setItems((prev) =>
      prev
        .map((p) => (p.key === key ? { ...p, qty: p.qty + delta } : p))
        .filter((p) => p.qty > 0)
    );
  }, [fitsBatch]);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((p) => p.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((a, b) => a + b.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((a, b) => a + b.unit_price * b.qty, 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, open, setOpen, checkoutIntent, setCheckoutIntent, startWhatsAppCheckout, addItem, updateQty, removeItem, clear, count, subtotal }),
    [items, open, setOpen, checkoutIntent, setCheckoutIntent, startWhatsAppCheckout, addItem, updateQty, removeItem, clear, count, subtotal],
  );
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
